import { Input, IconButton as MuiIconButton, Tooltip } from '@mui/joy';
import { memo, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import useToggle from '../../hooks/UseToggle';

import { ClearIcon, SearchIcon } from '../../icons/Icons';
import IconButton from '../buttons/IconButton';
import useDebouncedHandler from '@/shared/hooks/useDebouncedHandler';

type Props = {
  className?: string;
  fixed?: boolean;
  placeholder?: string;
  searchTerm?: string;
  setSearchText: (searchText: string) => void;
};

const DataTableSearch: React.FC<Props> = ({
  className,
  fixed,
  placeholder,
  searchTerm,
  setSearchText,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const handleSearch = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(e.target.value),
  );
  const searchToggle = useToggle(!!searchTerm);

  const handleBlur = () => {
    if (!inputRef.current) return;

    if (!inputRef.current.value) searchToggle.hide();
  };

  const handleClear = () => {
    if (!inputRef.current) return;

    const newValue = '';

    inputRef.current.value = newValue;
    setSearchText(newValue);
    inputRef.current.focus();
  };

  return (
    <>
      {!searchToggle.value && !fixed && (
        <Tooltip title={t('search.label')}>
          <MuiIconButton
            className={classNames('align-self-center aspect-square')}
            onClick={searchToggle.show}
          >
            <SearchIcon />
          </MuiIconButton>
        </Tooltip>
      )}

      {(searchToggle.value || fixed) && (
        <Input
          autoFocus={!fixed}
          className={className}
          defaultValue={searchTerm}
          placeholder={placeholder || t('search.placeholder')}
          slotProps={{ input: { ref: inputRef } }}
          endDecorator={<IconButton onClick={handleClear} iconComponent={<ClearIcon />} />}
          startDecorator={<SearchIcon />}
          onBlur={handleBlur}
          onChange={handleSearch}
        />
      )}
    </>
  );
};

export default memo(DataTableSearch);
